<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
        Users
        <BaseInfoIcon :text="ERKLAERUNG.benutzerListe" label="Users" />
      </h2>
    </div>

    <!-- Filters -->
    <div class="mb-6 flex flex-col md:flex-row md:items-end md:space-x-6">
      <!-- Session selector -->
      <div class="w-full md:w-1/4">
        <label class="block mb-2 font-semibold">Select Session</label>
        <BaseSelect
          v-model="selectedSessionId"
          :options="sessionOptions"
        />
      </div>

      <!-- Team selector -->
      <div class="w-full md:w-1/4 mt-4 md:mt-0">
        <label class="block mb-2 font-semibold">Select Team</label>
        <BaseSelect
          v-model="selectedTeamId"
          :options="teamOptions"
        />
      </div>
    </div>

    <!-- Users list -->
    <ul class="divide-y divide-gray-200">
      <li
        v-for="user in filteredUsers"
        :key="user.id"
        class="py-4 flex items-center justify-between"
      >
        <div>
          <p
            class="text-lg font-medium text-gray-900 cursor-pointer hover:underline"
            @click="$router.push({ path: `/users/${user.id}`, query: { session: selectedSessionId } })"
          >
            {{ user.first_name }} {{ user.last_name }} - {{ user.email }}

            <!-- Role badge -->
            <span
              class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium"
              :class="userRoles[user.id]?.name
                ? 'bg-indigo-100 text-indigo-800'
                : 'bg-gray-100 text-gray-500'"
            >
              {{ userRoles[user.id]?.name || 'No Role yet' }}
            </span>

            <!-- Team badge -->
            <span
              class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium"
              :class="userTeams[user.id]?.names && userTeams[user.id]?.names.length > 0
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-500'"
            >
              {{ userTeams[user.id]?.names && userTeams[user.id]?.names.length > 0 
                  ? userTeams[user.id].names.join(', ') 
                  : 'No Team yet'}}
            </span>
          </p>
        </div>

      </li>
    </ul>

    <!-- Empty state -->
    <p v-if="filteredUsers.length === 0" class="text-gray-500 mt-4 text-center">
      No users found.
    </p>

  </div>
</template>

<script>
import { getStudentUsers } from "@/api/users";
import { getUserSession } from "@/live-sessions/api/usersessions";
import { getLiveSessions } from "@/live-sessions/api/sessions";
import { getTeams } from "@/live-sessions/api/teams";
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";

export default {
  components: { BaseSelect, BaseInfoIcon },
  data() {
    return {
      ERKLAERUNG,
      users: [],
      sessions: [],
      teams: [],
      selectedSessionId: null,
      selectedTeamId: "", // team filter
      userRoles: {},
      userTeams: {},
    };
  },
  computed: {
    sessionOptions() {
      const flattenSessions = (sessions, depth = 0) => {
        return sessions.flatMap(s => [
          { value: s.id.toString(), label: `${"— ".repeat(depth)}${s.title}` },
          ...(s.children ? flattenSessions(s.children, depth + 1) : []),
        ]);
      };
      return flattenSessions(this.sessions);
    },
    teamOptions() {
      return [
        { value: "", label: "All Teams" },
        { value: "no-team", label: "No Team" },
        ...this.teams.map(team => ({ value: team.id.toString(), label: team.name })),
        
      ];
    },
    filteredUsers() {
      let filtered = this.users;

      if (this.selectedTeamId) {
        if (this.selectedTeamId === "no-team") {
          filtered = filtered.filter(user => !this.userTeams[user.id]?.ids || this.userTeams[user.id]?.ids.length === 0);
        } else {
          filtered = filtered.filter(
            user => this.userTeams[user.id]?.ids && this.userTeams[user.id]?.ids.includes(Number(this.selectedTeamId))
          );
        }
      }

      // Sort by primary team name, then last name
      return filtered.sort((a, b) => {
        const teamA = this.userTeams[a.id]?.names?.[0] || "";
        const teamB = this.userTeams[b.id]?.names?.[0] || "";
        const cmp = teamA.localeCompare(teamB, "en", { sensitivity: "base" });
        if (cmp !== 0) return cmp;
        return a.last_name.localeCompare(b.last_name, "en", { sensitivity: "base" });
      });
    },
  },
  watch: {
    async selectedSessionId(newSessionId) {
      if (newSessionId) {
        await this.fetchRolesForUsers(newSessionId);
      }
    },
  },
  methods: {
    async fetchSessions() {
      try {
        const res = await getLiveSessions();
        this.sessions = res.data;
      } catch (err) {
        console.error("Failed to load sessions:", err);
      }
    },
    async fetchTeams() {
      try {
        const res = await getTeams();
        this.teams = res.data;
      } catch (err) {
        console.error("Failed to load teams:", err);
      }
    },
    async fetchUsers() {
      try {
        const res = await getStudentUsers();
        this.users = res.data.sort((a, b) => {
          const last = a.last_name.localeCompare(b.last_name, "en", { sensitivity: "base" });
          return last !== 0 ? last : a.first_name.localeCompare(b.first_name, "en", { sensitivity: "base" });
        });
        if (this.selectedSessionId) {
          await this.fetchRolesForUsers(this.selectedSessionId);
        }
      } catch (err) {
        console.error("Failed to load users:", err);
      }
    },
    async fetchRolesForUsers(sessionId) {
      if (!sessionId || !this.users.length) return;

      const roles = {};
      const teams = {};
      
      await Promise.all(
        this.users.map(async (u) => {
          try {
            const res = await getUserSession(u.id, sessionId);
            const userSession = res.data;

            // Load role
            roles[u.id] = {
              id: userSession.role_id || null,
              name: userSession.role_name || null,
            };

            // Load teams (now supports multiple)
            teams[u.id] = {
              ids: userSession.team_ids || [],
              names: userSession.team_names || [],
            };
          } catch (err) {
            roles[u.id] = { id: null, name: null };
            teams[u.id] = { ids: [], names: [] };
          }
        })
      );

      this.userRoles = roles;
      this.userTeams = teams;
    },
  },
  async mounted() {
    await Promise.all([this.fetchSessions(), this.fetchTeams(), this.fetchUsers()]);

    if (this.sessions.length > 0) {
      this.selectedSessionId = this.sessions[0].id.toString();
    }
    await this.fetchUsers();
    this.selectedTeamId = "";
  },
};
</script>
